import React from 'react';
import { Document, View, Text, StyleSheet } from '@react-pdf/renderer';
import type { TimetablePrint, TimetablePrintCell } from '../../../services/timetableService';
import {
  Page, SummaryLine, pdfColors, pdfLetter, pdfSpacing, fmtDate,
  type SchoolBranding,
} from '../../../utils/pdf';

interface Props {
  print: TimetablePrint;
  branding: SchoolBranding;
  generatedAt: string;
}

type PrintPage = TimetablePrint['pages'][number];
type Lesson = Extract<TimetablePrintCell, { kind: 'lesson' }>;

// A4 landscape inside the page frame.
const CONTENT_W = 762;
const DAY_W = 68;
const BREAK_W = 16;
const GAP = 2;
/**
 * Subject colours on a printed page — well apart, so no two subjects on a
 * sheet look alike: an accent for the card's edge and its soft wash. Given
 * out in the order subjects first appear; the key below says which is which.
 */
const PALETTE = [
  { accent: '#2563eb', tint: '#eff6ff' }, { accent: '#059669', tint: '#ecfdf5' }, { accent: '#7c3aed', tint: '#f5f3ff' },
  { accent: '#d97706', tint: '#fffbeb' }, { accent: '#e11d48', tint: '#fff1f2' }, { accent: '#0891b2', tint: '#ecfeff' },
  { accent: '#65a30d', tint: '#f7fee7' }, { accent: '#c026d3', tint: '#fdf4ff' }, { accent: '#ea580c', tint: '#fff7ed' },
  { accent: '#4f46e5', tint: '#eef2ff' }, { accent: '#0d9488', tint: '#f0fdfa' }, { accent: '#be123c', tint: '#fff1f2' },
  { accent: '#475569', tint: '#f8fafc' },
];
/** What a class, a teacher and a room each do with a period. */
const BUSY_WORD: Record<TimetablePrint['view'], string> = { class: 'a week', teacher: 'taught', room: 'booked' };

type Colors = { accent: string; tint: string };
const NO_SUBJECT: Colors = { accent: pdfColors.textFaint, tint: pdfColors.surfaceSoft };

/** The height the day rows share, under the letterhead, summary, column heads and legend. */
const ROWS_H = 318;

const styles = StyleSheet.create({
  grid: { borderWidth: 0.6, borderColor: pdfColors.divider, borderStyle: 'solid', borderRadius: 5, overflow: 'hidden' },
  head: { flexDirection: 'row', backgroundColor: pdfColors.textPrimary },
  headDay: { width: DAY_W },
  headPeriod: { paddingVertical: 6, paddingHorizontal: 6 },
  headLabel: { fontSize: 7.5, fontWeight: 700, color: pdfColors.textOnDark, letterSpacing: 0.2 },
  headTime: { fontSize: 6.5, color: '#cbd5e1', marginTop: 1.5 },
  headBreak: { width: BREAK_W },
  row: { flexDirection: 'row', borderTopWidth: 0.6, borderTopColor: pdfColors.divider, borderTopStyle: 'solid' },
  rowAlt: { backgroundColor: '#fcfdfe' },
  day: { width: DAY_W, justifyContent: 'center', paddingHorizontal: 9, backgroundColor: pdfColors.surfaceSoft, borderRightWidth: 0.6, borderRightColor: pdfColors.divider, borderRightStyle: 'solid' },
  dayName: { fontSize: 8.5, fontWeight: 700, color: pdfColors.textPrimary },
  slot: { padding: GAP },
  card: { flex: 1, overflow: 'hidden', borderRadius: 3.5, paddingVertical: 4, paddingLeft: 7, paddingRight: 4, borderLeftWidth: 2.4, borderLeftStyle: 'solid' },
  // Text keeps its size in a full card; what doesn't fit is cut short with an ellipsis instead.
  fit: { flexShrink: 0, textOverflow: 'ellipsis' },
  subject: { fontSize: 8, fontWeight: 700, color: pdfColors.textPrimary },
  line: { fontSize: 6.6, color: pdfColors.textSecondary, marginTop: 1.6 },
  double: { fontSize: 5.6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: pdfLetter.openLabel, marginTop: 'auto' },
  off: { flex: 1, borderRadius: 3.5, backgroundColor: pdfColors.neutralSoft },
  brk: { width: BREAK_W, backgroundColor: '#f1f5f9', alignItems: 'center', justifyContent: 'center' },
  brkText: { width: 60, textAlign: 'center', fontSize: 5.6, fontWeight: 700, color: pdfColors.textFaint, letterSpacing: 1.2, textTransform: 'uppercase', transform: 'rotate(-90deg)' },
  legend: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', marginTop: pdfSpacing.sm },
  legendTitle: { fontSize: 6.3, fontWeight: 700, textTransform: 'uppercase', letterSpacing: pdfLetter.openLabel, color: pdfColors.textMuted, marginRight: 8 },
  legendItem: { flexDirection: 'row', alignItems: 'center', marginRight: 11, marginVertical: 2 },
  legendDot: { width: 7, height: 7, borderRadius: 2, marginRight: 4 },
  legendLabel: { fontSize: 7, color: pdfColors.textSecondary },
});

/** One school day: its lessons as cards in their subject's colour, a double period across two columns. */
const DayRow: React.FC<{ row: PrintPage['rows'][number]; periodW: number; height: number; alt: boolean; colorOf: (subjectId: string | null) => Colors }> = ({ row, periodW, height, alt, colorOf }) => {
  // Narrow columns (many periods) take smaller type; a short row shows fewer lines under the subject.
  const scale = Math.max(0.78, Math.min(1, periodW / 84));
  const subjectSize = 8 * scale;
  const lineSize = 6.6 * scale;
  // Height left for the lines under the subject — whether the subject itself wraps depends on its length.
  const linesFit = (c: Lesson) => {
    const textW = periodW * c.span - 2 * GAP - 11;
    const titleLines = c.title.length * subjectSize * 0.56 > textW ? 2 : 1;
    const room = height - 2 * GAP - 8 - titleLines * subjectSize * 1.3 - (c.span === 2 ? 8 : 0);
    return Math.max(1, Math.floor(room / (lineSize * 1.45)));
  };
  return (
  <View style={[styles.row, alt ? styles.rowAlt : {}, { height }]} wrap={false}>
    <View style={styles.day}>
      <Text style={styles.dayName}>{row.day}</Text>
    </View>
    {row.cells.map((c, i) => {
      if (c.kind === 'break') return <View key={i} style={styles.brk}>{c.label && <Text style={styles.brkText}>{c.label}</Text>}</View>;
      if (c.kind === 'free') return <View key={i} style={[styles.slot, { width: periodW }]} />;
      if (c.kind === 'off') return <View key={i} style={[styles.slot, { width: periodW }]}><View style={styles.off} /></View>;
      const colors = colorOf(c.subjectId);
      return (
        <View key={i} style={[styles.slot, { width: periodW * c.span }]}>
          <View style={[styles.card, { backgroundColor: colors.tint, borderLeftColor: colors.accent }]}>
            <Text style={[styles.subject, styles.fit, { fontSize: subjectSize, maxLines: 2 }]}>{c.title}</Text>
            {c.lines.slice(0, linesFit(c)).map(l => (
              <Text key={l} style={[styles.line, styles.fit, { fontSize: lineSize, maxLines: 1 }]}>{l}</Text>
            ))}
            {c.span === 2 && <Text style={[styles.double, styles.fit, { color: colors.accent }]}>Double period</Text>}
          </View>
        </View>
      );
    })}
  </View>
  );
};

/** A week on one page: the letterhead, how full it is, the grid, and a key to the subject colours. */
const TimetablePage: React.FC<{ page: PrintPage; print: TimetablePrint; branding: SchoolBranding; generatedAt: string }> = ({ page, print, branding, generatedAt }) => {
  const periods = print.columns.filter(c => c.kind === 'period').length;
  const breaks = print.columns.length - periods;
  const periodW = (CONTENT_W - DAY_W - breaks * BREAK_W) / Math.max(periods, 1);
  const rowH = Math.min(74, ROWS_H / Math.max(page.rows.length, 1));
  const state = print.status === 'draft' ? 'Draft' : print.publishedAt ? `Published ${fmtDate(print.publishedAt)}` : 'Published';

  const lessons = page.rows.flatMap(r => r.cells).filter((c): c is Lesson => c.kind === 'lesson');
  const taught = lessons.reduce((n, l) => n + l.span, 0);
  const free = page.rows.flatMap(r => r.cells).filter(c => c.kind === 'free').length;
  // One key entry per subject, in the order it first appears in the week.
  const subjects = new Map<string, string>();
  for (const l of lessons) if (l.subjectId && l.subject && !subjects.has(l.subjectId)) subjects.set(l.subjectId, l.subject);
  const keyed = Array.from(subjects, ([id, name]) => ({ id, name }));
  const palette = new Map(keyed.map((s, i) => [s.id, PALETTE[i % PALETTE.length]]));
  const colorOf = (id: string | null) => (id && palette.get(id)) || NO_SUBJECT;

  return (
    <Page
      branding={branding}
      orientation="landscape"
      meta={{ title: `Timetable — ${page.title}`, subtitle: state, generatedAt }}
      official={{ details: [{ value: 'Weekly timetable' }, { value: state }] }}
    >
      <SummaryLine
        groups={[{
          label: 'This week',
          items: [
            { label: `${taught === 1 ? 'Period' : 'Periods'} ${BUSY_WORD[print.view]}`, value: taught },
            { label: keyed.length === 1 ? 'Subject' : 'Subjects', value: keyed.length },
            { label: free === 1 ? 'Free period' : 'Free periods', value: free },
            { label: 'Days', value: page.rows.length },
          ],
        }]}
      />
      <View style={styles.grid} wrap={false}>
        <View style={styles.head}>
          <View style={styles.headDay} />
          {print.columns.map((c, i) => (c.kind === 'break'
            ? <View key={i} style={styles.headBreak} />
            : (
              <View key={i} style={[styles.headPeriod, { width: periodW }]}>
                <Text style={styles.headLabel}>{c.label}</Text>
                <Text style={styles.headTime}>{c.start} – {c.end}</Text>
              </View>
            )))}
        </View>
        {page.rows.map((r, i) => <DayRow key={r.day} row={r} periodW={periodW} height={rowH} alt={i % 2 === 1} colorOf={colorOf} />)}
      </View>
      {keyed.length > 0 && (
        <View style={styles.legend} wrap={false}>
          <Text style={styles.legendTitle}>Subjects</Text>
          {keyed.map(s => (
            <View key={s.id} style={styles.legendItem}>
              <View style={[styles.legendDot, { backgroundColor: colorOf(s.id).accent }]} />
              <Text style={styles.legendLabel}>{s.name}</Text>
            </View>
          ))}
        </View>
      )}
    </Page>
  );
};

/**
 * Timetables to print — one landscape page per class, teacher or room, under
 * the school's letterhead: days down the side, periods across with their
 * times, breaks as slim columns, every lesson a card in its subject's colour
 * (the same colours as the board), and a key to those colours.
 */
const TimetableDocument: React.FC<Props> = ({ print, branding, generatedAt }) => (
  <Document title={print.pages.length === 1 ? `Timetable — ${print.pages[0].title}` : `Timetables — ${print.pages.length} pages`} author={branding.name}>
    {print.pages.map(p => <TimetablePage key={p.title} page={p} print={print} branding={branding} generatedAt={generatedAt} />)}
  </Document>
);

export default TimetableDocument;
